import { memo, useState } from 'react';
import { CARD_IMG_BASE, cardSrc } from './cards/cardUtils';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const CARD_RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function findByNat(sub, nat) {
  return sub.find((s) => s.nat === nat);
}

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

// range collapse (float-right dtl20info)
function Range({ rangeId, min, max }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="float-right dtl20info">
      <i
        data-toggle="collapse"
        data-target={`#${rangeId}`}
        className="fas fa-info-circle"
        onClick={() => setOpen((o) => !o)}
      ></i>
      {' '}
      <div id={rangeId} className={`collapse icon-range${open ? ' show' : ''}`}>
        R:
        <span>{fmtAmt(min)}</span>
        -
        <span>{fmtAmt(max)}</span>
      </div>
    </div>
  );
}

// row descriptors — left column (0-8), right column (0-8)
const LEFT_ROWS = [
  { id: 0, kind: 'label', label: 'Winner', get: 'winner', max: 100000 },
  { id: 1, kind: 'black', get: 'black', max: 5000 },
  { id: 2, kind: 'red', get: 'red', max: 5000 },
  { id: 3, kind: 'label', label: 'Odd', get: 'odd', max: 5000 },
  { id: 4, kind: 'label', label: 'Even', get: 'even', max: 5000 },
  { id: 5, kind: 'card', rank: 'A', cardIdx: 0, max: 5000 },
  { id: 6, kind: 'card', rank: '2', cardIdx: 1, max: 5000 },
  { id: 7, kind: 'card', rank: '3', cardIdx: 2, max: 5000 },
  { id: 8, kind: 'card', rank: '4', cardIdx: 3, max: 5000 },
];
const RIGHT_ROWS = CARD_RANKS.slice(4).map((rank, i) => ({
  id: i, kind: 'card', rank, cardIdx: i + 4, max: 5000,
}));

const BetTableDTL20 = memo(function BetTableDTL20({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const [activeTab, setActiveTab] = useState('dragon');

  // per-outcome market lookups (D/T/L)
  const markets = {
    winner: {
      d: findByNat(sub, 'Winner D') || findByNat(sub, 'Dragon'),
      t: findByNat(sub, 'Winner T') || findByNat(sub, 'Tiger'),
      l: findByNat(sub, 'Winner L') || findByNat(sub, 'Lion'),
    },
    black: {
      d: findByNat(sub, 'Black D') || findByNat(sub, 'Dragon Black'),
      t: findByNat(sub, 'Black T') || findByNat(sub, 'Tiger Black'),
      l: findByNat(sub, 'Black L') || findByNat(sub, 'Lion Black'),
    },
    red: {
      d: findByNat(sub, 'Red D') || findByNat(sub, 'Dragon Red'),
      t: findByNat(sub, 'Red T') || findByNat(sub, 'Tiger Red'),
      l: findByNat(sub, 'Red L') || findByNat(sub, 'Lion Red'),
    },
    odd: {
      d: findByNat(sub, 'Odd D') || findByNat(sub, 'Dragon Odd'),
      t: findByNat(sub, 'Odd T') || findByNat(sub, 'Tiger Odd'),
      l: findByNat(sub, 'Odd L') || findByNat(sub, 'Lion Odd'),
    },
    even: {
      d: findByNat(sub, 'Even D') || findByNat(sub, 'Dragon Even'),
      t: findByNat(sub, 'Even T') || findByNat(sub, 'Tiger Even'),
      l: findByNat(sub, 'Even L') || findByNat(sub, 'Lion Even'),
    },
  };
  const cards = {
    d: CARD_RANKS.map((r) => findByNat(sub, `Dragon ${r}`)),
    t: CARD_RANKS.map((r) => findByNat(sub, `Tiger ${r}`)),
    l: CARD_RANKS.map((r) => findByNat(sub, `Lion ${r}`)),
  };

  const pick = (row, o) => (row.kind === 'card' ? cards[o][row.cardIdx] : markets[row.get][o]);

  // nation-name cell (label / suit image / card image)
  const nameCell = (row) => {
    if (row.kind === 'label') return <div className="casino-nation-name"><b>{row.label}</b></div>;
    if (row.kind === 'black') {
      return (
        <div className="casino-nation-name casino-card-img">
          <span><img src={`${CARD_IMG_BASE}/spade.png`} /> <img src={`${CARD_IMG_BASE}/club.png`} /></span>
        </div>
      );
    }
    if (row.kind === 'red') {
      return (
        <div className="casino-nation-name casino-card-img">
          <span><img src={`${CARD_IMG_BASE}/heart.png`} /> <img src={`${CARD_IMG_BASE}/diamond.png`} /></span>
        </div>
      );
    }
    return (
      <div className="casino-nation-name casino-card-img">
        <span><img src={cardSrc(row.rank)} /></span>
      </div>
    );
  };

  // back box — bookPrefix 'casino-book' (desktop) or '' (mobile)
  const backCell = (item, bookPrefix, key) => {
    const book = bookOf(exposures, item?.nat, item?.sid);
    const bookClass = bookPrefix ? `${bookPrefix} ${bookCls(book)}` : bookCls(book);
    return (
      <div className="casino-bl-box" key={key}>
        <div
          className={`back casino-bl-box-item${suspCls(item)}`}
          onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
        >
          <span className="casino-box-odd">{item?.b ?? 0}</span>
          {' '}
          <span className={bookClass}>{fmtBook(book)}</span>
        </div>
      </div>
    );
  };

  // desktop row: name + range + D/T/L boxes
  const desktopRow = (row, side) => (
    <div className="casino-box-row" key={`${side}-${row.id}`}>
      {nameCell(row)}
      {' '}
      <Range rangeId={`demo-${side}-${row.id}`} min={100} max={row.max} />
      {' '}
      {backCell(pick(row, 'd'), 'casino-book', 'd')}
      {backCell(pick(row, 't'), 'casino-book', 't')}
      {backCell(pick(row, 'l'), 'casino-book', 'l')}
    </div>
  );

  // mobile row (single outcome column): name + range + one box
  const mobileRow = (row, side, tab) => (
    <div className="casino-box-row" key={`${side}-${tab}-${row.id}`}>
      {nameCell(row)}
      {' '}
      <Range rangeId={`demo-${side}-${tab}-${row.id}`} min={100} max={row.max} />
      {' '}
      {backCell(pick(row, tab), '', 'b')}
    </div>
  );

  const mobileTab = (tab) => (
    <div className="teen1daycasino-container">
      <div className="teen1dayleft">
        {LEFT_ROWS.map((row) => mobileRow(row, 'l', tab))}
      </div>
      <div className="teen1daycenter"></div>
      <div className="teen1dayright">
        {RIGHT_ROWS.map((row) => mobileRow(row, 'r', tab))}
      </div>
    </div>
  );

  const TABS = [
    { key: 'dragon', tab: 'd', label: 'Dragon', id: 'dragon' },
    { key: 'tiger', tab: 't', label: 'Tiger', id: 'tiger' },
    { key: 'lion', tab: 'l', label: 'Lion', id: 'lion' },
  ];

  return (
    <>
      {/* ── MOBILE: tabbed layout ── */}
      <div className="d-none-big">
        <div className="casino-tabs">
          <ul role="tablist" className="nav nav-tabs">
            {TABS.map((t) => (
              <li className="nav-item" key={t.key}>
                <a
                  href={`#${t.id}`}
                  data-toggle="tab"
                  className={`nav-link${activeTab === t.key ? ' active' : ''}`}
                  onClick={(e) => { e.preventDefault(); setActiveTab(t.key); }}
                >
                  {t.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="tab-content">
          {TABS.map((t) => (
            <div id={t.id} className={`tab-pane${activeTab === t.key ? ' active' : ''}`} key={t.key}>
              {mobileTab(t.tab)}
            </div>
          ))}
        </div>
      </div>

      {/* ── DESKTOP: 3-column grid ── */}
      <div className="teen1daycasino-container d-none-small">
        <div className="teen1dayleft">
          <div className="casino-box-row mb-0">
            <div className="casino-nation-name no-border"><div></div></div>
            <div className="casino-bl-box"><b>D</b></div>
            <div className="casino-bl-box"><b>T</b></div>
            <div className="casino-bl-box"><b>L</b></div>
          </div>
          {LEFT_ROWS.map((row) => desktopRow(row, 'l'))}
        </div>
        <div className="teen1daycenter"></div>
        <div className="teen1dayright">
          <div className="casino-box-row mb-0 d-none-small">
            <div className="casino-nation-name no-border"><div></div></div>
            <div className="casino-bl-box"><b>D</b></div>
            <div className="casino-bl-box"><b>T</b></div>
            <div className="casino-bl-box"><b>L</b></div>
          </div>
          {RIGHT_ROWS.map((row) => desktopRow(row, 'r'))}
        </div>
      </div>
    </>
  );
});

export default BetTableDTL20;
